/**
 * M1 — Journal de mandat consultable.
 *
 * Consume `decisionsHistory` du store et affiche tour par tour les
 * décisions prises avec leur kicker + label de l'option choisie.
 * Remplace le placeholder vide qui annonçait depuis 6 sprints
 * « Aucune entrée. Le journal se remplira… ».
 *
 * Cf. project_retention_doctrine, levier #7 — matérialisation de
 * l'investissement accumulé du joueur.
 */

import { useMemo, useState } from "react";
import { useGame } from "../store/useGame";
import type { Decision } from "../engine/types";

const MONTHS = [
  "janv.",
  "févr.",
  "mars",
  "avril",
  "mai",
  "juin",
  "juil.",
  "août",
  "sept.",
  "oct.",
  "nov.",
  "déc.",
];

function formatTurnDate(turn: number): string {
  // Mandat démarre à T1 = janvier 2028.
  const m = (turn - 1) % 12;
  const y = 2028 + Math.floor((turn - 1) / 12);
  return `${MONTHS[m]} ${y}`;
}

interface JournalEntry {
  turn: number;
  archetypeKicker: string;
  archetypePrompt: string;
  optionLabels: string[];
  tags: string[];
}

function buildEntries(
  decisionsHistory: string[][],
  decisions: Decision[],
): JournalEntry[] {
  // Index par option_id : { archetype, option_label, tags }.
  const optionIndex = new Map<
    string,
    { kicker: string; prompt: string; label: string; tags: string[] }
  >();
  for (const d of decisions) {
    const tags = d.tags ?? [];
    for (const o of d.options ?? []) {
      optionIndex.set(o.id, {
        kicker: d.kicker ?? "",
        prompt: d.prompt ?? "",
        label: o.label ?? o.id,
        tags,
      });
    }
  }

  const entries: JournalEntry[] = [];
  decisionsHistory.forEach((turnChoices, idx) => {
    if (!turnChoices || turnChoices.length === 0) return;
    const turn = idx + 1;
    const first = optionIndex.get(turnChoices[0]);
    entries.push({
      turn,
      archetypeKicker: first?.kicker ?? "—",
      archetypePrompt: first?.prompt ?? "(décision sans contexte)",
      optionLabels: turnChoices.map(
        (oid) => optionIndex.get(oid)?.label ?? oid,
      ),
      tags: first?.tags ?? [],
    });
  });
  // Le plus récent en haut.
  return entries.reverse();
}

export function JournalDrawer() {
  const decisionsHistory = useGame((s) => s.decisionsHistory);
  const decisions = useGame((s) => s.decisions);
  const [activeTag, setActiveTag] = useState<string | null>(null);

  const entries = useMemo(
    () => buildEntries(decisionsHistory, decisions),
    [decisionsHistory, decisions],
  );

  // QW3 — agrégation des tags présents dans le journal, triés par fréquence
  // décroissante, top 8 affichés. Permet un filtrage instantané du journal.
  const topTags = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of entries) {
      for (const t of e.tags) counts.set(t, (counts.get(t) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([tag, n]) => ({ tag, n }));
  }, [entries]);

  const filtered = activeTag
    ? entries.filter((e) => e.tags.includes(activeTag))
    : entries;

  return (
    <details className="drawer drawer-appear">
      <summary>
        Journal{" "}
        {entries.length > 0 && (
          <span className="journal-count">· {entries.length}</span>
        )}
      </summary>
      <div className="drawer-body">
        {entries.length === 0 ? (
          <p className="journal-empty">
            Aucune entrée — votre mandat n'a pas encore commencé.
          </p>
        ) : (
          <>
            {topTags.length > 0 && (
              <div className="journal-tag-filters" aria-label="Filtre par tag">
                <button
                  type="button"
                  className={`journal-tag-chip ${activeTag === null ? "is-active" : ""}`}
                  onClick={() => setActiveTag(null)}
                >
                  toutes
                </button>
                {topTags.map(({ tag, n }) => (
                  <button
                    key={tag}
                    type="button"
                    className={`journal-tag-chip ${activeTag === tag ? "is-active" : ""}`}
                    onClick={() =>
                      setActiveTag((cur) => (cur === tag ? null : tag))
                    }
                  >
                    #{tag} <span className="journal-tag-n">{n}</span>
                  </button>
                ))}
              </div>
            )}
            <ol className="journal-list">
              {filtered.map((e) => (
                <li key={e.turn} className="journal-entry">
                  <div className="journal-meta">
                    <span className="journal-turn">tour {e.turn}</span>
                    <span className="journal-date">
                      {formatTurnDate(e.turn)}
                    </span>
                  </div>
                  {e.archetypeKicker && (
                    <div className="journal-kicker">{e.archetypeKicker}</div>
                  )}
                  <div className="journal-prompt">{e.archetypePrompt}</div>
                  <ul className="journal-options">
                    {e.optionLabels.map((lbl, i) => (
                      <li key={i}>✓ {lbl}</li>
                    ))}
                  </ul>
                  {e.tags.length > 0 && (
                    <div className="journal-entry-tags">
                      {e.tags.slice(0, 4).map((t) => (
                        <span key={t} className="journal-tag-mini">
                          #{t}
                        </span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </>
        )}
      </div>
    </details>
  );
}
